import { DatePipe } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isComplexValue } from '@modules/filter/utils/filter-changes';
import { Filter, FilterBean, FilterType } from '@type/filter.type';
import { FilterDictionariesService } from '../filter-dictionaries/filter-dictionaries.service';
import { FilterService } from '../filter/filter.service';

export interface Chips {
  view: string;
  value: any;
  delete(): void;
}

@Injectable({
  providedIn: 'root',
})
export class FilterChipsService {
  private fs = inject(FilterService);

  private fds = inject(FilterDictionariesService);

  private date = inject(DatePipe);

  private translations = {
    empty: 'Empty',
    filled: 'Filled',
    system: 'System',
    yes: 'Yes',
    no: 'No',
    insight: 'One',
  };

  constructor() {
    this.fs.changes.pipe(takeUntilDestroyed()).subscribe((res) => this.initChips(res));
  }

  chips = signal<Chips[]>([]);

  clearAll() {
    this.chips.set([]);
  }

  create() {
    return { name: 'string' };
  }

  add = (chips: Chips[]) => {
    const current = this.chips();
    const unique = chips.filter((chip) => !current.includes(chip));
    if (unique.length) {
      this.chips.set([...current, ...unique]);
    }
  };

  delete = (chips: Chips[]) => {
    const updated = this.chips().filter((chip) => !chips.includes(chip));
    this.chips.set(updated);
  };

  initChips(filter: [string, FilterBean][]) {
    this.clearAll();
    this.createInitialChips(filter);
  }

  createInitialChips(filter: [keyof Filter, Filter['key']][]) {
    Array.from(filter).forEach(async ([k, v]) => {
      if (isComplexValue(v)) {
        switch (v['type']) {
          case FilterType.Reference:
            await this.createReferenceChips(v);
            break;
          case FilterType.Boolean:
            this.createBooleanChips(v);
            break;
          case FilterType.Date:
            this.createDateChips(v);
            break;
          case FilterType.String:
          case FilterType.User:
            this.createStringChips(v, v['type']);
            break;
          case FilterType.Number:
            this.createNumberChips(v);
            break;
          case FilterType.Sku:
            this.createSkuChips(v);
            break;
          case FilterType.Select:
            this.createSelectChips(v);
            break;
          default:
            this.createSkuChips(v);
            break;
        }
      }
    });
  }

  private async createSelectChips(filter: FilterBean) {
    if (Array.isArray(filter.value)) {
      const translations = await this.getTranslationsForSelect(filter.dictionaryModel);
      const arr = this.createChipsFromArray(filter.value || [], filter, translations);

      this.add(arr);
    }
  }

  private createNumberChips(filter: FilterBean) {
    const { is_null, from, to, not_null } = filter;
    const arr = [];
    const fs = this.fs;
    let str = '';

    if (Number.isFinite(from) && Number.isFinite(to)) {
      str = `${from} - ${to}`;
    }

    if (Number.isFinite(from) && !Number.isFinite(to)) {
      str = `≥ ${from}`;
    }

    if (!Number.isFinite(from) && Number.isFinite(to)) {
      str = `≤ ${to}`;
    }

    str = str.length > 12 ? str.substring(0, 12) + '...' : str;

    !!str &&
      arr.push({
        view: filter.name + ': ' + str,
        value: filter,
        delete() {
          filter.to = '';
          filter.from = '';
          fs.tick();
        },
      });

    is_null && arr.push(this.createIsNullChips(filter));
    not_null && arr.push(this.createNotNullChips(filter));

    this.add(arr);
  }

  private createDateChips(filter: FilterBean) {
    const { is_null, from, to, not_null } = filter;
    const arr = [];
    const fs = this.fs;
    let str = '';

    if (from && to) {
      str = `${this.date.transform(from)} - ${this.date.transform(to)}`;
    }

    if (from && !to) {
      str = `≥ ${this.date.transform(from)}`;
    }

    if (!from && to) {
      str = `≤ ${this.date.transform(to)}`;
    }

    str = str.length > 12 ? str.substring(0, 12) + '...' : str;

    !!str &&
      arr.push({
        view: filter.name + ': ' + str,
        value: filter,
        delete() {
          filter.to = '';
          filter.from = '';
          fs.tick();
        },
      });

    is_null && arr.push(this.createIsNullChips(filter));
    not_null && arr.push(this.createNotNullChips(filter));

    this.add(arr);
  }

  private async createReferenceChips(filter: FilterBean) {
    if (Array.isArray(filter.value)) {
      const translations = !!filter.dictionaryModel ? await this.getTranslations(filter.dictionaryModel) : {};
      const arr = this.createChipsFromArray(filter.value, filter, translations);
      if (filter.is_null) arr.push(this.createIsNullChips(filter));
      if (filter.not_null) arr.push(this.createNotNullChips(filter));

      this.add(arr);
    }
  }

  private createBooleanChips(filter: FilterBean) {
    if (Array.isArray(filter.value)) {
      const arr = this.createChipsFromArray(filter.value, filter, {
        true: this.translations.yes,
        false: this.translations.no,
      });

      if (filter.is_null) arr.push(this.createIsNullChips(filter));
      if (filter.not_null) arr.push(this.createNotNullChips(filter));

      this.add(arr);
    }
  }

  private createStringChips(filter: FilterBean, type: FilterType.String | FilterType.User) {
    const fs = this.fs;
    const translation = this.translations[type === FilterType.String ? 'empty' : 'system'];
    const isNull = this.createIsNullChips(filter, translation);
    const value = {
      view: filter.name + ': ' + filter.value,
      value: filter.value,
      delete() {
        filter.value = '';
        fs.tick();
      },
    };

    if (filter.is_null && filter.value) {
      this.add([value, isNull]);
      return;
    }

    if (filter.value) {
      this.add([value]);
      return;
    }

    if (filter.is_null) {
      this.add([isNull]);
      return;
    }

    if (filter.not_null) {
      this.add([this.createNotNullChips(filter)]);
      return;
    }
  }

  private createSkuChips(filter: FilterBean) {
    Array.isArray(filter.value) && this.add(this.createChipsFromArray(filter.value, filter));
  }

  private createIsNullChips(filter: { name: string; is_null: boolean }, translation = ''): Chips {
    const fs = this.fs;
    return {
      view: filter.name + ': ' + (translation || this.translations.empty),
      value: filter.is_null,
      delete() {
        filter.is_null = false;
        fs.tick();
      },
    };
  }

  private createNotNullChips(filter: { name: string; not_null: boolean }, translation = ''): Chips {
    const fs = this.fs;
    return {
      view: filter.name + ': ' + (translation || this.translations.filled),
      value: filter.not_null,
      delete() {
        filter.not_null = false;
        fs.tick();
      },
    };
  }

  private createChipsFromArray(
    array: any[],
    filter: { name: string },
    translations: Record<string, string> = {},
  ): Chips[] {
    const fs = this.fs;
    if (!array) return [];

    return array.map((v) => ({
      view: filter.name + ': ' + (translations[v] || v),
      value: v,
      delete() {
        array.splice(array.indexOf(this.value), 1);
        fs.tick();
      },
    }));
  }

  private async getTranslations(modelName: string) {
    const { dict } = await this.fds.getFullDictionary(modelName);
    return Object.fromEntries(dict.map((v) => [v.id, v.name]));
  }

  private async getTranslationsForSelect(modelName: string) {
    const { dict } = await this.fds.getSelectDictionary(modelName);
    return Object.fromEntries(dict.map((v) => [v.id, v.name]));
  }
}
